import {
  condenseSectorSummaries,
  condenseStatusSummaries,
  type PipelineSummaryItem,
} from "../../pages/pipeline/pipelineSummaryCondensed";
import type { FunnelSummaryResponse } from "../../types/dealFunnel";
import type { Status } from "../../types/deal";
import type { SponsorTypePipelineSummaryResponse } from "../../utils/sponsorSummaryHelpers";
import { isKilledStatus } from "../../utils/dealStatusHelpers";
import { addDealFlowOverviewSlide } from "./dealFlowOverviewSlide";
import { addDealFlowQuarterlySlide } from "./dealFlowQuarterlySlide";
import {
  addMarketOriginationSlide,
  buildOriginationChannels,
} from "./marketOriginationSlide";
import {
  configureDeck,
  createPresentation,
  loadTenfiftyLogoDataUri,
} from "./pptxCore";
import { addStatusSectorSlide } from "./statusSectorSlide";

type PipelineSummaryResponse = {
  summaries: PipelineSummaryItem[];
  total?: {
    deal_count: number;
    loan_amount_gbp: number;
    average_deal_size_gbp?: number;
  };
};

export type TenFiftyPptSummaryFixtures = {
  dealFlowOverall: FunnelSummaryResponse;
  dealFlowQuarterly: FunnelSummaryResponse;
  statusSummary: PipelineSummaryResponse;
  sectorSummary: PipelineSummaryResponse;
  sourceFunnel: FunnelSummaryResponse;
  statuses: Status[];
  sponsorSummary: SponsorTypePipelineSummaryResponse;
  logoDataUri?: string | null;
};

/** Market slide covers the closed book plus deals in closing; Closed - Apollo stays out. */
const MARKET_SLIDE_STATUSES = new Set([
  "closed",
  "ic memo",
  "preview memo",
  "signed / committed",
]);

function marketStatusIds(statuses: Status[]): number[] {
  return statuses
    .filter((s) => MARKET_SLIDE_STATUSES.has(s.status.trim().toLowerCase()))
    .map((s) => s.id);
}

/** Status IDs to include in the Status & Sector slide sector breakdown (excludes killed). */
function nonKilledStatusIds(statuses: Status[]): number[] {
  return statuses
    .filter((s) => !isKilledStatus(s.status))
    .map((s) => s.id);
}

async function fetchLiveFixtures(): Promise<TenFiftyPptSummaryFixtures> {
  // Dynamic import keeps offline fixture smoke tests free of browser API modules.
  const { apiGet } = await import("../genesisApi/httpsMethods");

  async function fetchPipelineSummary(
    groupBy: string,
    statusIds: number[] = [],
  ): Promise<PipelineSummaryResponse> {
    let url = `deals/pipeline/summary?group_by=${groupBy}`;
    if (statusIds.length > 0) {
      url += `&${statusIds.map((id) => `status_ids=${id}`).join("&")}`;
    }
    const response = await apiGet<PipelineSummaryResponse>(url);
    if (!response.success || !response.data) {
      throw new Error(`Failed to load ${groupBy} pipeline summary.`);
    }
    return response.data;
  }

  async function fetchFunnelSummary(
    groupBy: "overall" | "source",
    options: { period?: "quarter"; statusIds?: number[] } = {},
  ): Promise<FunnelSummaryResponse> {
    let url = `deals/funnel/summary?group_by=${groupBy}`;
    if (options.period) url += `&period=${options.period}`;
    for (const id of options.statusIds ?? []) {
      url += `&status_ids=${id}`;
    }

    const response = await apiGet<FunnelSummaryResponse>(url);
    if (!response.success || !response.data) {
      throw new Error(`Failed to load funnel summary (${groupBy}).`);
    }
    return response.data;
  }

  async function fetchStatuses(): Promise<Status[]> {
    const response = await apiGet<Status[]>("status");
    if (!response.success || !response.data) {
      throw new Error("Failed to load statuses.");
    }
    return response.data;
  }

  const [dealFlowOverall, dealFlowQuarterly, statusSummary, statuses] =
    await Promise.all([
      fetchFunnelSummary("overall"),
      fetchFunnelSummary("overall", { period: "quarter" }),
      fetchPipelineSummary("status"),
      fetchStatuses(),
    ]);

  const marketIds = marketStatusIds(statuses);
  const nonKilledIds = nonKilledStatusIds(statuses);
  const sponsorUrl =
    marketIds.length > 0
      ? `deals/pipeline/summary?group_by=sponsor_type&${marketIds
          .map((id) => `status_ids=${id}`)
          .join("&")}`
      : "deals/pipeline/summary?group_by=sponsor_type";

  const [sectorSummary, sourceFunnel, sponsorResponse] = await Promise.all([
    fetchPipelineSummary("sector", nonKilledIds),
    fetchFunnelSummary("source", { statusIds: marketIds }),
    apiGet<SponsorTypePipelineSummaryResponse>(sponsorUrl),
  ]);
  if (!sponsorResponse.success || !sponsorResponse.data) {
    throw new Error("Failed to load closed sponsor-type summary.");
  }

  return {
    dealFlowOverall,
    dealFlowQuarterly,
    statusSummary,
    sectorSummary,
    sourceFunnel,
    statuses,
    sponsorSummary: sponsorResponse.data,
  };
}

/**
 * Build and download the four-slide TenFifty PPT Summary deck.
 * Pass `fixtures` to skip network (offline smoke tests).
 */
export async function exportTenFiftyPptSummary(
  fixtures?: TenFiftyPptSummaryFixtures,
): Promise<void> {
  try {
    const data = fixtures ?? (await fetchLiveFixtures());
    const dealFlow = data.dealFlowOverall.deal_flow;
    if (!dealFlow) {
      throw new Error(
        "Deal Flow data is not available for the overall funnel. Cannot export PPT summary.",
      );
    }

    const statusItems = condenseStatusSummaries(
      data.statusSummary.summaries ?? [],
    );
    const sectorItems = condenseSectorSummaries(
      data.sectorSummary.summaries ?? [],
    );
    const statusTotal = data.statusSummary.total ?? {
      deal_count: statusItems.reduce((s, r) => s + r.deal_count, 0),
      loan_amount_gbp: statusItems.reduce((s, r) => s + r.loan_amount_gbp, 0),
    };
    const sectorTotal = data.sectorSummary.total ?? {
      deal_count: sectorItems.reduce((s, r) => s + r.deal_count, 0),
      loan_amount_gbp: sectorItems.reduce((s, r) => s + r.loan_amount_gbp, 0),
    };

    const { channels, totalVolume: channelTotalVolume } =
      buildOriginationChannels(data.sourceFunnel.buckets ?? []);

    const sponsors = [...(data.sponsorSummary.summaries ?? [])].sort(
      (a, b) => b.loan_amount_gbp - a.loan_amount_gbp,
    );
    const sponsorTotalVolume =
      data.sponsorSummary.total?.loan_amount_gbp ??
      sponsors.reduce((sum, row) => sum + row.loan_amount_gbp, 0);

    const logoDataUri =
      fixtures?.logoDataUri !== undefined
        ? fixtures.logoDataUri
        : await loadTenfiftyLogoDataUri();

    const pptx = createPresentation();
    configureDeck(pptx);

    addDealFlowOverviewSlide(pptx, dealFlow, logoDataUri);
    addDealFlowQuarterlySlide(pptx, data.dealFlowQuarterly, logoDataUri);
    addStatusSectorSlide(
      pptx,
      {
        statusItems,
        statusTotal,
        sectorItems,
        sectorTotal,
      },
      logoDataUri,
    );
    addMarketOriginationSlide(
      pptx,
      {
        channels,
        channelTotalVolume,
        sponsors,
        sponsorTotalVolume,
      },
      logoDataUri,
    );

    const datetime = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    await pptx.writeFile({
      fileName: `TenFifty_PPT_Summary_${datetime}.pptx`,
    });
  } catch (error) {
    console.error("Error exporting TenFifty PPT Summary:", error);
    if (error instanceof Error && error.message.includes("Deal Flow data")) {
      throw error;
    }
    throw new Error(
      "Failed to export TenFifty PPT Summary. Please try again.",
    );
  }
}
